<div class="dash">

  <div class="dash-head">
    <div class="dash-head-left">
      <div class="dash-eyebrow">Dashboards / Operations overview</div>
      <h1 class="dash-title">
        {{ greeting() }}{{ userName() ? ', ' + userName() : '' }}
        <span class="dash-live">
          <span class="dash-live-dot"></span>
          LIVE · {{ locationName() || 'All locations' }}
          @if (locationType()) { · {{ locationType() }} }
        </span>
      </h1>
    </div>
    <div class="dash-head-actions">
      @if (loadedAt()) {
        <span class="dash-updated">Updated {{ loadedAt() | date : 'HH:mm' }}</span>
      }
      <button type="button" class="btn btn-secondary btn-sm" (click)="refresh()" [disabled]="loading()">
        <i class="bi bi-arrow-repeat" [class.dash-spin]="loading()"></i> Refresh
      </button>
    </div>
  </div>

  <!-- KPI strip -->
  @if (kpis().length) {
    <section class="dash-kpis">
      @for (k of kpis(); track k.label) {
        <a class="dash-kpi" [class.dark]="k.dark" [routerLink]="k.route" [queryParams]="k.queryParams">
          <div class="dash-kpi-top">
            <span class="dash-kpi-label">{{ k.label }}</span>
            @if (k.tone) {
              <span class="dash-kpi-tone" [ngClass]="k.tone"></span>
            }
          </div>
          <span class="dash-kpi-value">{{ k.value }}</span>
          <span class="dash-kpi-meta">{{ k.meta }}</span>
        </a>
      }
    </section>
  } @else if (!loading()) {
    <section class="dash-card">
      <div class="zf-empty">
        <strong>Nothing to show yet</strong>
        <span>Your role has no access to the purchasing or stock modules this overview summarises.</span>
      </div>
    </section>
  }

  <div class="dash-grid">
    <!-- Left column -->
    <div class="dash-col">

      @if (pipeline().length) {
        <section class="dash-card">
          <div class="dash-card-head">
            <div>
              <h2>Inbound pipeline</h2>
              <span class="dash-sub">PO → receipt → transfer → store</span>
            </div>
            <a routerLink="/po" class="dash-link">Open purchase orders →</a>
          </div>
          <div class="dash-pipeline">
            @for (p of pipeline(); track p.label; let last = $last) {
              <a class="dash-stage" [ngClass]="p.tone" [routerLink]="p.route">
                <span class="dash-stage-label">{{ p.label }}</span>
                <span class="dash-stage-value">{{ p.value }}</span>
                <span class="dash-stage-meta">{{ p.meta }}</span>
              </a>
              @if (!last) { <span class="dash-stage-arrow">›</span> }
            }
          </div>
        </section>
      }

      @if (snap()?.activePos) {
        <section class="dash-card">
          <div class="dash-card-head">
            <div>
              <h2>Recent purchase orders</h2>
              <span class="dash-sub">Latest active orders</span>
            </div>
            <a routerLink="/po/create-po" class="btn btn-sm btnprimary"><i class="bi bi-plus-lg"></i> Create PO</a>
          </div>
          @if (recentPos().length) {
            <div class="dash-table">
              <div class="dash-row dash-row-head">
                <span>PO number</span><span>Vendor</span><span>Order date</span><span class="r">Items</span><span class="r">Amount</span><span>Status</span>
              </div>
              @for (po of recentPos(); track po.id) {
                <a class="dash-row" [routerLink]="['/po/edit-po', po.id]" [queryParams]="{ view: true }">
                  <span class="mono key">{{ po.poNumber }}</span>
                  <span class="ellipsis">{{ po.vendor?.name || '—' }}</span>
                  <span class="mono dim">{{ po.orderDate | date : 'dd-MM-yy' }}</span>
                  <span class="mono r">{{ po.totalItems || 0 }}</span>
                  <span class="mono r strong">{{ po.totalAmount | number : '1.0-0' }}</span>
                  <span><span class="zf-status" [ngClass]="poTone(po.status)">{{ po.status }}</span></span>
                </a>
              }
            </div>
          } @else {
            <div class="zf-empty"><strong>No active purchase orders</strong><span>Create a PO to start the inbound flow.</span></div>
          }
        </section>
      }

      @if (snap()?.grns) {
        <section class="dash-card">
          <div class="dash-card-head">
            <div>
              <h2>Recent receipts</h2>
              <span class="dash-sub">Latest goods receipt notes</span>
            </div>
            <a routerLink="/grn/create-grn" class="btn btn-sm btn-secondary"><i class="bi bi-box-arrow-in-down"></i> Receive goods</a>
          </div>
          @if (recentGrns().length) {
            <div class="dash-table">
              <div class="dash-row dash-row-head">
                <span>GRN number</span><span>Vendor</span><span>Received</span><span class="r">Items</span><span class="r">Amount</span><span>Status</span>
              </div>
              @for (g of recentGrns(); track g.id) {
                <a class="dash-row" [routerLink]="['/grn/edit', g.id]" [queryParams]="{ view: true }">
                  <span class="mono key">{{ g.grnNumber }}</span>
                  <span class="ellipsis">{{ g.vendorName || g.vendor?.name || '—' }}</span>
                  <span class="mono dim">{{ g.receivedDate | date : 'dd-MM-yy' }}</span>
                  <span class="mono r">{{ g.purchaseOrder?.totalItems || g.totalItems || 0 }}</span>
                  <span class="mono r strong">{{ g.totalAmount || 0 | number : '1.0-0' }}</span>
                  <span><span class="zf-status" [ngClass]="poTone(g.status)">{{ g.status }}</span></span>
                </a>
              }
            </div>
          } @else {
            <div class="zf-empty"><strong>No receipts yet</strong><span>Posted GRNs will appear here.</span></div>
          }
        </section>
      }
    </div>

    <!-- Right column -->
    <div class="dash-col dash-col-side">
      <section class="dash-card">
        <div class="dash-card-head">
          <div>
            <h2>Needs attention</h2>
            <span class="dash-sub">{{ attention().length }} open</span>
          </div>
        </div>
        @if (attention().length) {
          <div class="dash-attn">
            @for (a of attention(); track a.title) {
              <a class="dash-attn-row" [routerLink]="a.route" [queryParams]="a.queryParams">
                <span class="dash-attn-dot" [ngClass]="a.tone"></span>
                <span class="dash-attn-body">
                  <span class="dash-attn-title">{{ a.title }}</span>
                  <span class="dash-attn-meta">{{ a.meta }}</span>
                </span>
                <span class="zf-status" [ngClass]="a.tone === 'neutral' ? '' : a.tone">{{ a.badge }}</span>
              </a>
            }
          </div>
        } @else if (!loading()) {
          <div class="zf-empty"><strong>All clear</strong><span>Nothing is waiting on you right now.</span></div>
        }
      </section>

      @if (stock(); as s) {
        <section class="dash-card">
          <div class="dash-card-head">
            <div>
              <h2>Stock health</h2>
              <span class="dash-sub">{{ s.location?.name }}</span>
            </div>
            <a routerLink="/inventory/list" class="dash-link">Stock status →</a>
          </div>
          <div class="dash-stock">
            <div class="dash-stock-row">
              <span>On hand</span>
              <strong class="mono">{{ s.onHandQty | number : '1.0-0' }}</strong>
            </div>
            @if (s.location?.type === 'Warehouse') {
              <div class="dash-stock-row">
                <span>In transit to stores <em>· {{ s.inTransitOutboundDocs || 0 }} TO</em></span>
                <strong class="mono">{{ s.inTransitOutboundQty | number : '1.0-0' }}</strong>
              </div>
            } @else {
              <div class="dash-stock-row">
                <span>In transit from warehouse</span>
                <strong class="mono">{{ s.inTransitInboundQty | number : '1.0-0' }}</strong>
              </div>
            }
            @for (area of s.subAreas || []; track area.id) {
              <div class="dash-stock-row" [class.danger]="area.type === 'DamageArea' && area.qty > 0">
                <span>{{ area.name }} <em>· {{ area.skuCount }} SKU{{ area.skuCount === 1 ? '' : 's' }}</em></span>
                <strong class="mono">{{ area.qty | number : '1.0-0' }}</strong>
              </div>
            }
            @if (snap()?.lowStock; as low) {
              <div class="dash-stock-row" [class.danger]="low.length > 0">
                <span>Below reorder level</span>
                <strong class="mono">{{ low.length }}</strong>
              </div>
            }
          </div>
        </section>
      }

      <section class="dash-card">
        <div class="dash-card-head">
          <div>
            <h2>Quick actions</h2>
            <span class="dash-sub">Common tasks</span>
          </div>
        </div>
        <div class="dash-actions">
          <a routerLink="/po/create-po" class="dash-action"><i class="bi bi-cart-plus"></i><span>Create purchase order</span></a>
          <a routerLink="/grn/create-grn" class="dash-action"><i class="bi bi-box-arrow-in-down"></i><span>Receive goods</span></a>
          <a routerLink="/transfer-out/create" class="dash-action"><i class="bi bi-truck"></i><span>New transfer out</span></a>
          <a routerLink="/stocktake/create" class="dash-action"><i class="bi bi-clipboard-check"></i><span>Start stock take</span></a>
          <a routerLink="/stock-write-off/create" class="dash-action"><i class="bi bi-x-octagon"></i><span>Write off stock</span></a>
          <a routerLink="/add-product" class="dash-action"><i class="bi bi-box-seam"></i><span>Add product</span></a>
        </div>
      </section>
    </div>
  </div>
</div>
